"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";

export default function ProfileParallaxImage({
  className = "",
  scale = 1.08,
  travel = 44,
  ...imageProps
}) {
  const imageRef = useRef(null);

  useEffect(() => {
    const image = imageRef.current;
    const container = image?.parentElement;
    if (!image || !container) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      image.style.transform = `scale(${scale})`;
      return undefined;
    }

    let animationFrame;

    const update = () => {
      animationFrame = undefined;
      const bounds = container.getBoundingClientRect();
      const range = window.innerHeight + bounds.height;
      const progress = Math.min(
        Math.max((window.innerHeight - bounds.top) / range, 0),
        1,
      );
      const offset = (progress - 0.5) * travel;
      image.style.transform = `translate3d(0, ${offset}px, 0) scale(${scale})`;
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [scale, travel]);

  return (
    <Image
      ref={imageRef}
      {...imageProps}
      fill
      className={`${className} will-change-transform`}
    />
  );
}
